'use client'

import { type ReactNode, useState } from 'react'
import Link from 'next/link'
import { useLocale, useTranslations } from 'next-intl'

import { MarketingModulePreTitle } from '@/components/marketing/marketing-module-pretitle'
import { Container } from '@/components/ui/container'
import { Button } from '@/components/ui/button'
import { Section } from '@/components/ui/section'
import { TypographyH2, TypographyP } from '@/components/ui/typography'
import { pathWithLocale } from '@/lib/i18n/locale-path'
import { paths } from '@/lib/routes/site-paths'
import { cn } from '@/lib/utils'

type ProofTab = 'stories' | 'benchmarks'

function ProofTabTrigger({
  active,
  children,
  onSelect,
}: {
  active: boolean
  children: ReactNode
  onSelect: () => void
}) {
  return (
    <Button
      type="button"
      role="tab"
      aria-selected={active}
      variant={active ? 'outlineStrong' : 'secondaryLight'}
      size="md"
      className={cn(
        'h-auto min-h-0 rounded-sm px-8 py-4 font-mono text-xs uppercase tracking-widest shadow-none',
        '!border-x-0 !border-t-0 !border-b-2 !bg-transparent',
        active
          ? '!border-b-charcoal-900 font-bold !text-charcoal-900 hover:!bg-charcoal-900 hover:!text-white'
          : '!border-transparent !text-charcoal-500 hover:!border-gray-300 hover:!text-charcoal-900',
      )}
      onClick={onSelect}
    >
      {children}
    </Button>
  )
}

export function SolutionsIndexProofSection() {
  const t = useTranslations('solutionsPage')
  const locale = useLocale()
  const [tab, setTab] = useState<ProofTab>('stories')
  const referenceHref = `${pathWithLocale(locale, paths.contact)}?type=reference-call`

  return (
    <Section tone="white" border="bGray200" className="py-16 md:py-24 lg:py-32">
      <Container size="site" className="text-center">
        <MarketingModulePreTitle tone="charcoal500">{t('proof.pre')}</MarketingModulePreTitle>
        <TypographyH2 scale="lg" className="mx-auto mb-12 max-w-4xl text-charcoal-900">
          {t('proof.title')}
        </TypographyH2>

        <div
          role="tablist"
          aria-label={t('proof.title')}
          className="mx-auto mb-10 flex max-w-2xl justify-center border-b border-gray-200"
        >
          <ProofTabTrigger active={tab === 'stories'} onSelect={() => setTab('stories')}>
            {t('proof.tab1')}
          </ProofTabTrigger>
          <ProofTabTrigger active={tab === 'benchmarks'} onSelect={() => setTab('benchmarks')}>
            {t('proof.tab2')}
          </ProofTabTrigger>
        </div>

        <TypographyP
          size="lead"
          tone="subdued"
          className="mx-auto mb-10 max-w-2xl text-lg font-light leading-relaxed text-charcoal-600"
        >
          {t('proof.desc')}
        </TypographyP>

        <Button
          variant="outlineStrong"
          size="md"
          asChild
          className="rounded-sm text-fs-xl font-bold tracking-widest"
        >
          <Link href={referenceHref}>{t('proof.btn')}</Link>
        </Button>
      </Container>
    </Section>
  )
}
